// Institución (tenant) activa, resuelta en tiempo de ejecución contra la API.
//
// La API decide el modo por el host: si el host está mapeado a una institución (iush.edu.co,
// salazaryherrera.edu.co) es modo "dominio" y la URL no lleva prefijo; cualquier otro host es
// modo "plataforma" y el primer segmento de la ruta es el slug de la institución. Aquí se
// consulta GET /api/instituciones/actual, se deriva la paleta, se inyectan las variables CSS
// (las mismas que consumía index.css) y se fija el X-Institucion de apiFetch.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getInstitucionActual } from '../services/institucionesService';
import { setInstitucionActiva } from '../services/apiFetch';
import { derivarPaleta } from '../utils/paleta';
import { construirRutaPublica, primerSegmento } from '../utils/rutaPublica';

const InstitucionContext = createContext(null);

const NOMBRE_MARCA_DEFECTO = 'PUSAH';
const CLAVE_DOMINIO = '__dominio__';

const ESTADO_INICIAL = {
  cargando: true,
  error: null,
  modo: null,
  institucion: null,
  nombreMarca: NOMBRE_MARCA_DEFECTO,
  urlMarca: null,
};

// Variables CSS que consume index.css (.app-header) más título y meta description.
const aplicarTema = (institucion, paleta, nombreMarca) => {
  const root = document.documentElement;
  const variables = {
    '--color-primary': paleta.primary,
    '--color-primary-dark': paleta.primaryDark,
    '--color-primary-light': paleta.primaryLight,
    '--header-gradient-start': paleta.headerGradientStart,
    '--header-gradient-mid': paleta.headerGradientMid,
    '--header-gradient-end': paleta.headerGradientEnd,
    '--header-border': paleta.headerBorder,
    '--header-shadow-primary': paleta.headerShadowPrimary,
    '--header-shadow-secondary': paleta.headerShadowSecondary,
    '--header-dark-gradient-start': paleta.headerDarkGradientStart,
    '--header-dark-gradient-mid': paleta.headerDarkGradientMid,
    '--header-dark-gradient-end': paleta.headerDarkGradientEnd,
    '--header-dark-border': paleta.headerDarkBorder,
  };
  Object.entries(variables).forEach(([nombre, valor]) => root.style.setProperty(nombre, valor));

  document.body.setAttribute('data-institution', institucion ? institucion.slug : 'pusah');

  document.title = institucion
    ? institucion.tituloPagina || `Publicaciones Virtuales — ${institucion.nombreCorto}`
    : `${nombreMarca} — Centro de Publicaciones`;

  const meta = document.querySelector('meta[name="description"]');
  if (meta) {
    meta.setAttribute(
      'content',
      (institucion && institucion.descripcion) || 'Centro de publicaciones digitales interactivas'
    );
  }
};

export const InstitucionProvider = ({ children }) => {
  const location = useLocation();
  const segmento = primerSegmento(location.pathname);

  // Slug fijado desde el panel admin: en modo plataforma la ruta /admin no lleva institución, y
  // un SuperAdmin puede elegir cualquier institución (también en modo dominio).
  const [slugAdmin, setSlugAdmin] = useState(null);
  const [estado, setEstado] = useState(ESTADO_INICIAL);
  const [version, setVersion] = useState(0);
  const versionCargadaDominio = useRef(-1);

  // En modo dominio el primer segmento es una categoría, no una institución: sólo cuenta el slug
  // fijado por el admin. Sin él, una sola carga sirve para toda la sesión. En plataforma se
  // recarga cuando cambia el slug de la ruta (o el fijado por el admin).
  const slugConsulta = estado.modo === 'dominio' ? slugAdmin : (segmento || slugAdmin);
  const clave = estado.modo === 'dominio' && !slugAdmin ? CLAVE_DOMINIO : (slugConsulta || '');

  useEffect(() => {
    if (clave === CLAVE_DOMINIO && versionCargadaDominio.current === version) return undefined;
    // Una carga con slug explícito invalida la carga "de dominio": al volver sin slug se recarga.
    if (clave !== CLAVE_DOMINIO) versionCargadaDominio.current = -1;

    let vigente = true;
    setEstado((prev) => ({ ...prev, cargando: true, error: null }));

    getInstitucionActual(clave === CLAVE_DOMINIO ? null : clave || null)
      .then((data) => {
        if (!vigente) return;
        if (data.modo === 'dominio' && clave === CLAVE_DOMINIO) versionCargadaDominio.current = version;
        // Antes de renderizar a los hijos: sus peticiones ya deben llevar X-Institucion.
        setInstitucionActiva(data.institucion ? data.institucion.slug : null);
        setEstado({
          cargando: false,
          error: null,
          modo: data.modo,
          institucion: data.institucion || null,
          nombreMarca: data.nombreMarca || NOMBRE_MARCA_DEFECTO,
          urlMarca: data.urlMarca || null,
        });
      })
      .catch(() => {
        if (!vigente) return;
        setEstado((prev) => ({
          ...prev,
          cargando: false,
          error: 'No se pudo conectar con el servicio de publicaciones. Intenta de nuevo en unos minutos.',
        }));
      });

    return () => {
      vigente = false;
    };
  }, [clave, version]);

  const paleta = useMemo(() => derivarPaleta(estado.institucion ? estado.institucion.colores : null), [estado.institucion]);

  useEffect(() => {
    if (!estado.cargando) aplicarTema(estado.institucion, paleta, estado.nombreMarca);
  }, [estado.cargando, estado.institucion, paleta, estado.nombreMarca]);

  const seleccionar = useCallback((slug) => setSlugAdmin(slug || null), []);
  const recargar = useCallback(() => setVersion((v) => v + 1), []);
  const ruta = useCallback(
    (path) => construirRutaPublica(estado.modo, estado.institucion ? estado.institucion.slug : null, path),
    [estado.modo, estado.institucion]
  );

  const value = useMemo(
    () => ({ ...estado, paleta, seleccionar, recargar, ruta }),
    [estado, paleta, seleccionar, recargar, ruta]
  );

  return <InstitucionContext.Provider value={value}>{children}</InstitucionContext.Provider>;
};

/**
 * @returns {{
 *   cargando: boolean, error: string|null, modo: 'dominio'|'plataforma'|null,
 *   institucion: object|null, nombreMarca: string, urlMarca: string|null,
 *   paleta: Record<string,string>, seleccionar: (slug: string|null) => void,
 *   recargar: () => void, ruta: (path: string) => string
 * }}
 */
export const useInstitucion = () => {
  const ctx = useContext(InstitucionContext);
  if (!ctx) throw new Error('useInstitucion debe usarse dentro de <InstitucionProvider>');
  return ctx;
};

export default InstitucionContext;
